import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useBranding } from "../components/Layout";
import { usePersonalization } from "../lib/personalization";
import { CheckCircle2, FileText, ArrowLeft } from "lucide-react";
import ListingInputForm from "../components/forms/ListingInputForm";
import HomeStoryForm from "../components/forms/HomeStoryForm";
import ShowingPrefsForm from "../components/forms/ShowingPrefsForm";
import BuyerInfoForm from "../components/forms/BuyerInfoForm";
import BuyerIntakeForm from "../components/forms/BuyerIntakeForm";
import BuyerPreferencesForm from "../components/forms/BuyerPreferencesForm";
import { useAuth } from "../lib/auth";
import MyForms from "../components/MyForms";

// Each form is tagged with a `phase` so the dashboard + this page can show
// only the forms relevant to the seller's current listing stage. Pre-listing
// forms are filed before the home goes live; under-contract forms (e.g. the
// Buyer Welcome Info packet) are collected after offer-acceptance.
// `side` tags decide whether a form belongs on the sell portal, the buy
// portal, or both.
const buildForms = (branding) => [
  { key: "listing_input",       side: "sell", phase: "pre_listing",    label: "Listing Input Form",    desc: "Property details + inclusions/exclusions for the MLS",                              Comp: ListingInputForm },
  { key: "home_story",          side: "sell", phase: "pre_listing",    label: "Your Home's Story",     desc: `A few quick questions so ${branding.agent_first_name} can best market your home`,    Comp: HomeStoryForm },
  { key: "showing_preferences", side: "sell", phase: "pre_listing",    label: "Showing Preferences",   desc: "How showings get handled",                                                          Comp: ShowingPrefsForm },
  { key: "buyer_info",          side: "sell", phase: "under_contract", label: "Buyer Welcome Info",    desc: "Trash day, garage code, alarm code, neighborhood tips — for the new owner",        Comp: BuyerInfoForm },
  { key: "buyer_intake",        side: "buy",  phase: "pre_listing",    label: "Get to know you",       desc: `A quick intro so ${branding.agent_first_name} can serve you well`,                  Comp: BuyerIntakeForm },
  { key: "buyer_preferences",   side: "buy",  phase: "pre_listing",    label: "What you're looking for", desc: "Budget, timeline, must-haves, dealbreakers",                                       Comp: BuyerPreferencesForm },
];

export default function Forms() {
  const branding = useBranding();
  const { user } = useAuth();
  const activeSide = user?.active_side || "sell";
  const { p } = usePersonalization();
  const isUnderContract = !!(p?.under_contract && p.under_contract.active);
  const ALL_FORMS = buildForms(branding);
  // Filter to (a) the side the buyer/seller is currently viewing and (b)
  // their current phase. Buy-side clients only ever see the two intake
  // forms; sell-side keeps its pre-listing / under-contract split.
  const FORMS = ALL_FORMS.filter((f) => {
    if (f.side !== activeSide) return false;
    if (activeSide === "buy") return true;
    return isUnderContract ? f.phase === "under_contract" : f.phase === "pre_listing";
  });
  const [active, setActive] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [toast, setToast] = useState(null);

  const load = async () => {
    const { data } = await api.get("/forms/my-submissions");
    setSubmissions(data.submissions || []);
  };
  useEffect(() => { load(); }, []);

  const onSubmitted = (result) => {
    load();
    setActive(null);
    const emailNote = result.email?.sent ? "PDF sent to your agent." : "Form saved. (Email pending agent email setup.)";
    setToast(`${result.form_title} submitted. ${emailNote}`);
    setTimeout(() => setToast(null), 4500);
  };

  const submittedMap = submissions.reduce((acc, s) => { acc[s.form_type] = s; return acc; }, {});

  if (active) {
    // Lookup against the full set so a form opened in one phase still works
    // if the seller's status flips while they have it open.
    const f = ALL_FORMS.find((x) => x.key === active);
    if (!f) { setActive(null); return null; }
    const Comp = f.Comp;
    return (
      <div className="container-x" style={{ padding: "48px 0 64px" }}>
        <button data-testid="forms-back-btn" className="btn-ghost" onClick={() => setActive(null)} style={{ marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <ArrowLeft size={16} /> Back to forms
        </button>
        <span className="overline">Form</span>
        <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 500, margin: "8px 0 24px", letterSpacing: "-0.01em" }}>{f.label}</h1>
        <Comp onSubmitted={onSubmitted} />
      </div>
    );
  }

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">Forms</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Paperwork, without the paper.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 680 }}>
        Fill out and submit. Each submission emails a polished PDF directly to your agent.
      </p>

      {toast && (
        <div data-testid="toast" style={{ marginTop: 24, padding: "12px 16px", borderRadius: 12, background: "#E7F0EA", color: "var(--success)", display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={18} /> {toast}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginTop: 40 }}>
        {FORMS.map((f) => {
          const done = submittedMap[f.key];
          return (
            <button key={f.key} data-testid={`form-card-${f.key}`} onClick={() => setActive(f.key)} className="card card-hover" style={{ padding: 28, textAlign: "left", cursor: "pointer", background: "#fff", border: "1px solid var(--border)", fontFamily: "inherit", color: "inherit" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <FileText size={22} style={{ color: "var(--primary)" }} />
                {done ? <span className="badge badge-success"><CheckCircle2 size={12} style={{ marginRight: 4 }} /> Submitted</span> : <span className="badge">Not yet</span>}
              </div>
              <h3 className="font-serif" style={{ fontSize: 24, marginTop: 16, fontWeight: 500, letterSpacing: "-0.01em" }}>{f.label}</h3>
              <p style={{ color: "var(--text-muted)", marginTop: 6, fontSize: 14 }}>{f.desc}</p>
              {done && <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>Last submitted {new Date(done.created_at).toLocaleDateString()}</div>}
            </button>
          );
        })}
      </div>

      <MyForms />
    </div>
  );
}
